import { api, type Page } from './client'

export interface Invoice {
  id: number
  invoiceNumber: string
  status: string // draft | sent | paid | cancelled
  invoiceDate: string
  dueDate?: string
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (TASK-0087 field-level redaction, both Get and List). */
  total?: number
  contractId?: number
  customerId?: number
  createdAt: string
}

export interface PaymentBatch {
  id: number
  batchCode: string
  paymentDate: string
  status: string // draft | confirmed | rejected
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (TASK-0087 field-level redaction, both Get and List). */
  totalAmount?: number
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (TASK-0087 field-level redaction, both Get and List). */
  netAmount?: number
  createdAt: string
}

export interface AgentCommission {
  id: number
  userId: number
  contractId: number
  productId?: number
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (redacted since TASK-0021). */
  commissionAmount?: number
  /** Omitted entirely by the backend (not null) for a caller without
   * CanSeeRealAmounts (redacted since TASK-0021). */
  paidAmount?: number
  status: string // pending | approved | paid | cancelled
  paidAt?: string
  batchId?: number
  createdAt: string
}

export const invoicesApi = {
  list: (p?: { status?: string; customerId?: number; contractId?: number; page?: number }) => {
    const q = new URLSearchParams()
    if (p?.status) q.set('status', p.status)
    if (p?.customerId) q.set('customerId', String(p.customerId))
    if (p?.contractId) q.set('contractId', String(p.contractId))
    q.set('page', String(p?.page ?? 1))
    q.set('size', '25')
    return api<Page<Invoice>>(`/api/invoices?${q}`)
  },
  updateStatus: (id: number, status: string) =>
    api<void>(`/api/invoices/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) }),
  delete: (id: number) => api<void>(`/api/invoices/${id}`, { method: 'DELETE' }),
}

export const paymentBatchesApi = {
  list: (p?: { status?: string; page?: number; sort?: string }) => {
    const q = new URLSearchParams()
    if (p?.status) q.set('status', p.status)
    // F-101: created_desc (default) | created_asc | status
    if (p?.sort) q.set('sort', p.sort)
    q.set('page', String(p?.page ?? 1))
    q.set('size', '25')
    return api<Page<PaymentBatch>>(`/api/payment-batches?${q}`)
  },
  confirm: (id: number, reason?: string) =>
    api<void>(`/api/payment-batches/${id}/confirm`, { method: 'PUT', body: JSON.stringify({ reason: reason ?? null }) }),
  reject: (id: number, reason: string) =>
    api<void>(`/api/payment-batches/${id}/reject`, { method: 'PUT', body: JSON.stringify({ reason }) }),
  delete: (id: number) => api<void>(`/api/payment-batches/${id}`, { method: 'DELETE' }),
}

// ── Expenses (TASK-0276/F-022, REQ-144-001/REQ-145-001) ──────────────────────────────────────────

/**
 * Mirrors `ExpensesController.Get`/`List`'s response shape field-for-field: both return the
 * `Expense` domain entity directly (no DTO/projection layer), so whatever it serializes to is
 * exactly this type. `availableTransitions` is `Expense.AvailableTransitions`
 * (`ExpenseStatusTransitions.ValidNextStatuses(Status)`, camelCased by ASP.NET Core's default MVC
 * JSON policy) — the SOLE source for which transition buttons `ExpensesPage.tsx` renders. No status
 * graph is duplicated here or anywhere else in this module; see `ExpensesPage.tsx`'s own remarks.
 */
export interface Expense {
  id: number
  supplierId?: number
  expenseDate: string
  description: string
  expenseCategoryId: number
  costCentre?: string
  /** F-064: centro di costo dell'anagrafica (`/api/finance/cost-centres`). */
  costCentreId?: number | null
  netAmount: number
  vatAmount: number
  totalAmount: number
  dueDate?: string
  status: string // Prevista | Approvata | DaPagare | ParzialmentePagata | Pagata | Annullata | Contestata | Scaduta
  expectedAccountId?: number
  notes?: string
  /** F-092: dimensioni analitiche (id di altri moduli); `null`/assenti = non assegnate. */
  operatorId?: number | null
  productId?: number | null
  campaignId?: number | null
  contestId?: number | null
  agentUserId?: number | null
  projectCode?: string | null
  /** R3-10 (flag finanza.dimensioni_estese): metodo di pagamento, BU, sede e team (responsabile). */
  paymentMethod?: string | null
  businessUnitId?: number | null
  siteId?: number | null
  teamId?: number | null
  /** Every status this expense may move to right now, per the server's own applied graph — empty
   * for both terminal statuses (Pagata/Annullata). See this file's own class-level remarks. */
  availableTransitions: string[]
  createdAt: string
  updatedAt?: string
}

/** Body for create — mirrors `ExpenseCreateRequest` field-for-field. */
export interface ExpenseCreateBody {
  supplierId?: number | undefined
  expenseDate: string
  description: string
  expenseCategoryId: number
  costCentre?: string | undefined
  netAmount: number
  vatAmount: number
  dueDate?: string | undefined
  expectedAccountId?: number | undefined
  notes?: string | undefined
  motivation?: string | undefined
  costCentreId?: number | undefined
  /** F-092: dimensioni analitiche. */
  operatorId?: number | undefined
  productId?: number | undefined
  campaignId?: number | undefined
  contestId?: number | undefined
  agentUserId?: number | undefined
  projectCode?: string | undefined
  /** R3-10 (flag finanza.dimensioni_estese): ignorati dal server a flag spento. */
  paymentMethod?: string | undefined
  businessUnitId?: number | undefined
  siteId?: number | undefined
  teamId?: number | undefined
}

/** Body for update — mirrors `ExpenseUpdateRequest` field-for-field; every field is optional
 * (null-means-untouched on the server, see `Expense.UpdateDetails`'s own remarks). */
export interface ExpenseUpdateBody {
  supplierId?: number | undefined
  expenseDate?: string | undefined
  description?: string | undefined
  expenseCategoryId?: number | undefined
  costCentre?: string | undefined
  netAmount?: number | undefined
  vatAmount?: number | undefined
  dueDate?: string | undefined
  expectedAccountId?: number | undefined
  notes?: string | undefined
  motivation?: string | undefined
  costCentreId?: number | undefined
  /** F-092: dimensioni analitiche. Omessa = non toccare; `0` (o codice progetto vuoto) = togliere. */
  operatorId?: number | undefined
  productId?: number | undefined
  campaignId?: number | undefined
  contestId?: number | undefined
  agentUserId?: number | undefined
  projectCode?: string | undefined
  /** R3-10 (flag finanza.dimensioni_estese): omessa = non toccare; `0` (o metodo vuoto) = togliere. */
  paymentMethod?: string | undefined
  businessUnitId?: number | undefined
  siteId?: number | undefined
  teamId?: number | undefined
}

export const expensesApi = {
  /** `[Authorize(PolicyNames.EqualStaff)]`. Filtri lato server (`status`/`supplierId`/`expenseCategoryId`
   * e, da F-101, `due` = all|overdue|none con `sort` = date_desc|date_asc|due_asc|due_desc|status|created_desc). */
  list: (p?: {
    /** F-101: ordinamento e scadenza lato server. */
    sort?: string | undefined
    due?: string | undefined
    status?: string | undefined
    supplierId?: number | undefined
    expenseCategoryId?: number | undefined
    page?: number | undefined
    size?: number | undefined
    /** F-092: filtri per dimensione analitica. */
    operatorId?: number | undefined
    productId?: number | undefined
    campaignId?: number | undefined
    contestId?: number | undefined
    agentUserId?: number | undefined
    projectCode?: string | undefined
  }) => {
    const q = new URLSearchParams()
    if (p?.status) q.set('status', p.status)
    if (p?.supplierId) q.set('supplierId', String(p.supplierId))
    if (p?.expenseCategoryId) q.set('expenseCategoryId', String(p.expenseCategoryId))
    if (p?.operatorId) q.set('operatorId', String(p.operatorId))
    if (p?.productId) q.set('productId', String(p.productId))
    if (p?.campaignId) q.set('campaignId', String(p.campaignId))
    if (p?.contestId) q.set('contestId', String(p.contestId))
    if (p?.agentUserId) q.set('agentUserId', String(p.agentUserId))
    if (p?.projectCode) q.set('projectCode', p.projectCode)
    if (p?.sort) q.set('sort', p.sort)
    if (p?.due && p.due !== 'all') q.set('due', p.due)
    q.set('page', String(p?.page ?? 1))
    q.set('size', String(p?.size ?? 25))
    return api<Page<Expense>>(`/api/expenses?${q}`)
  },
  /** `[Authorize(PolicyNames.EqualStaff)]`. */
  get: (id: number) => api<Expense>(`/api/expenses/${id}`),
  /** `[Authorize(PolicyNames.EqualStaff)]`. */
  create: (body: ExpenseCreateBody) =>
    api<{ id: number; possibleDuplicates?: number[] }>('/api/expenses', { method: 'POST', body: JSON.stringify(body) }),
  /** `[Authorize(PolicyNames.EqualStaff)]`. */
  update: (id: number, body: ExpenseUpdateBody) =>
    api<void>(`/api/expenses/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  /** `[Authorize(PolicyNames.Admin)]` — every target status, not a subset (see
   * `ExpensesController`'s own class remarks). Callers must gate this on `auth.role` before
   * offering the buttons (see `ExpensesPage.tsx`'s `isAdmin`); a non-Admin caller gets a 403. */
  transition: (id: number, status: string, motivation?: string) =>
    api<void>(`/api/expenses/${id}/transition`, {
      method: 'POST',
      body: JSON.stringify(motivation ? { status, motivation } : { status }),
    }),
  /** F-104 (REQ-287-001): NON cancella: sposta nel cestino una spesa ancora `Prevista` (`DELETE ?reason=`). Solo
   * FinanceAdministration (il server risponde 403 agli altri; la pagina lo gate-a con `canTransition`). 409 se la
   * spesa non e' Prevista o in un mese chiuso, 409 `HAS_DEPENDENCIES` con `dependencies` (approvazioni, movimenti
   * dei soci, quote bancarie) se ha dei collegamenti. Si ripristina da Impostazioni > Cestino. */
  delete: (id: number, reason?: string) => {
    const q = reason && reason.trim() ? `?reason=${encodeURIComponent(reason.trim())}` : ''
    return api<void>(`/api/expenses/${id}${q}`, { method: 'DELETE' })
  },
}

/** Mirrors `ExpenseCategory` field-for-field (REQ-142-001). */
export interface ExpenseCategory {
  id: number
  name: string
  isActive: boolean
  createdAt: string
  updatedAt?: string
}

export const expenseCategoriesApi = {
  /** `[Authorize(PolicyNames.Authenticated)]` — every logged-in caller needs the vocabulary to
   * register an expense. `includeInactive` defaults to false server-side, same as the backend. */
  list: (includeInactive?: boolean) =>
    api<ExpenseCategory[]>(`/api/expense-categories${includeInactive ? '?includeInactive=true' : ''}`),
  /** `[Authorize(PolicyNames.EqualStaff)]`. A duplicate name answers 400 with `{ error }`, surfaced
   * verbatim by `api()`. */
  create: (name: string) =>
    api<{ id: number }>('/api/expense-categories', { method: 'POST', body: JSON.stringify({ name }) }),
  /** `[Authorize(PolicyNames.EqualStaff)]` — also the "attiva/disattiva" action (`isActive`), same
   * endpoint as a rename; see `ExpenseCategoriesController.Update`. */
  update: (id: number, body: { name?: string | undefined; isActive?: boolean | undefined }) =>
    api<void>(`/api/expense-categories/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
}

/** NOT a server-side projection: `SuppliersController.List` returns the full `Supplier` entity
 * (`Ok(items)`, no DTO), so the real JSON response carries every field (VatNumber, Category,
 * ContactInfo, Iban, PaymentTerms, Frequency, CostCentre, Notes, IsActive, ...) — this type only
 * DECLARES the subset `ExpensesPage.tsx` actually reads to populate the "Fornitore" selector; the
 * rest arrives and is silently ignored by the client, same as any other over-fetch in this codebase.
 * Production has 0 rows today (verified before this task); the selector must render correctly with
 * zero options regardless. */
export interface SupplierOption {
  id: number
  companyName: string
}

export const suppliersApi = {
  /** `[Authorize(PolicyNames.Authenticated)]`; server already filters to `IsActive` rows only. */
  list: () => api<SupplierOption[]>('/api/suppliers'),
}

/** NOT a server-side projection either — same situation as `SupplierOption` above:
 * `FinancialAccountsController.List` returns the full `FinancialAccount` entity (`Ok(items)`), this
 * type only DECLARES the subset `ExpensesPage.tsx` reads to populate the "Conto previsto" selector.
 * Same zero-rows-in-production note as `SupplierOption`. */
export interface FinancialAccountOption {
  id: number
  name: string
}

export const financialAccountsApi = {
  /** `[Authorize(PolicyNames.Authenticated)]`; server already filters to `IsActive` rows only. */
  list: () => api<FinancialAccountOption[]>('/api/financial-accounts'),
}

export const agentCommissionsApi = {
  /** REQ-206-002: `AgentCommissionsController.List` is `[Authorize(PolicyNames.Authenticated)]`
   * (class-level) — an Agent-tier caller reaches it and gets back exactly the rows they may see
   * (their own, plus their team's when their `UserCategory.CanSeeSubordinates` is set), never the
   * whole table. `userId`/`contractId`/`status` are client conveniences layered on top of that
   * rule; they can only narrow it, never widen it. */
  list: (p?: { userId?: number; contractId?: number; status?: string; page?: number; sort?: string }) => {
    const q = new URLSearchParams()
    if (p?.userId) q.set('userId', String(p.userId))
    if (p?.contractId) q.set('contractId', String(p.contractId))
    if (p?.status) q.set('status', p.status)
    // F-101: created_desc (default) | created_asc | status
    if (p?.sort) q.set('sort', p.sort)
    q.set('page', String(p?.page ?? 1))
    q.set('size', '25')
    return api<Page<AgentCommission>>(`/api/agent-commissions?${q}`)
  },
  /** `[Authorize(PolicyNames.EqualStaff)]` — unchanged by REQ-206-002, which widened the READS
   * only. Callers must gate this on `auth.role` before offering it in the UI (see
   * `CommissionsPage.tsx`'s `isEqualStaff`); an Agent-tier caller gets a 403. */
  approve: (id: number) =>
    api<void>(`/api/agent-commissions/${id}/approve`, { method: 'PUT' }),
  /** `[Authorize(PolicyNames.Admin)]` — same note as `approve`, one tier narrower. */
  delete: (id: number) =>
    api<void>(`/api/agent-commissions/${id}`, { method: 'DELETE' }),
}
